import { v4 as uuidv4 } from "uuid";
import userImagePlaceholder1 from "@circle-react-shared/BuilderUI/Assets/user_1_image_placeholder.png";
import userImagePlaceholder2 from "@circle-react-shared/BuilderUI/Assets/user_2_image_placeholder.png";
import userImagePlaceholder3 from "@circle-react-shared/BuilderUI/Assets/user_3_image_placeholder.png";
import userImagePlaceholder4 from "@circle-react-shared/BuilderUI/Assets/user_4_image_placeholder.png";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { createCardBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers/cardBlockHelper";
import { createHrBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers/hrBlockHelper";
import { createUserCardBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers/userCardBlockHelper";
import {
  createBlockquoteBlock,
  createH2Block,
  createPBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import { getBackgroundImageOptions } from "@circle-react-shared/BuilderUI/utils/backgroundHelpers";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBackgroundColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getFontSizeOptions } from "../../../utils/textStyleHelpers";
import { primaryBlockDefaultStyles } from "../constants";

const cards = [
  {
    image: {
      signedId: "",
      placeholder: userImagePlaceholder1,
    },
    user: {
      name: "Sarah Johnson",
      position: "CEO, TechVision Inc.",
    },
    quote:
      "“Before joining Clarity Coaching, I struggled with imposter syndrome as a new CEO. The group coaching sessions gave me the support, confidence, and clarity I needed to succeed.”",
    description:
      "Results: Improved team retention by 35%, successful company pivot, increased revenue by $2.3M",
  },
  {
    image: {
      signedId: "",
      placeholder: userImagePlaceholder2,
    },
    user: {
      name: "Michael Chen",
      position: "SVP of Operations, Global Logistics",
    },
    quote:
      "“I was stuck in middle management for 5 years before joining the Clarity leadership Collective. The combination of expert coaching and peer-driven accountability accelerated my success. Within 90 days, I implemented a new operational framework that caught the attention of senior leadership, and within months, I was promoted to SVP.”",
    description:
      "Results: Promoted from Director to SVP, led successful international expansion, 42% salary increase",
  },
  {
    image: {
      signedId: "",
      placeholder: userImagePlaceholder3,
    },
    user: {
      name: "David Wilson",
      position: "CFO, Healthcare Innovation",
    },
    quote:
      "“I was a technical expert with zero leadership training when I was suddenly promoted to CFO. Being part of the Clarity community while receiving 1:1 coaching helped me develop the executive presence and strategic thinking skills I desperately needed. The combination of personal guidance and peer support made all the difference. I went from feeling overwhelmed to confidently leading our $50M fundraising round in just 4 months.”",
    description:
      "Results: Successfully transitioned from Finance Director to CFO, led $50M fundraising round, implemented new financial systems that saved $1.2M annually",
  },
  {
    image: {
      signedId: "",
      placeholder: userImagePlaceholder4,
    },
    user: {
      name: "Jennifer Martines",
      position: "Director of Sales, Retail Solutions",
    },
    quote:
      "“My team was underperforming and I was on the verge of being let go. The mastermind sessions transformed our dysfunctional team into a high-performing unit. We developed a shared vision and the communication tools needed to execute effectively, with ongoing support from both the coach and community members. Within one quarter, we went from the bottom-performing team to the top.“",
    description:
      "Results: 48% sales increase, reduced team conflict, improved customer satisfaction scores from 72% to 94%",
  },
] as const;

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "HomepageBlock7",
  props: {
    header: {
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: "Real Leaders. Real Growth",
        }),
        createPBlock({
          text: "From career pivots to executive breakthroughs, our coaching and community have empowered leaders to achieve their biggest goals.",
        }),
      ],
    },
    cards: {
      prop: "children",
      type: "children",
      value: cards.map(({ image, user, quote, description }) => {
        const value = [
          createUserCardBlock({
            image,
            header: user.name,
            description: user.position,
          }),
          createBlockquoteBlock({
            text: quote,
            styles: {
              fontSize: getFontSizeOptions("1.125rem"),
            },
          }),
          createHrBlock({
            styles: {
              padding: getPaddingOptions({
                defaultValue: {
                  paddingTop: "0",
                  paddingBottom: "0",
                  paddingLeft: "0",
                  paddingRight: "0",
                },
              }),
            },
          }),
          createPBlock({
            text: description,
            paragraphStyle: "p3",
          }),
        ];
        return createCardBlock({ value });
      }),
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        backgroundColor: getBackgroundColorOptions({
          defaultValue: primaryBlockDefaultStyles.backgroundColor,
        }),
        backgroundImage: getBackgroundImageOptions(),
        padding: getPaddingOptions({
          defaultValue: primaryBlockDefaultStyles.padding,
        }),
        margin: getMarginOptions({
          defaultValue: primaryBlockDefaultStyles.margin,
        }),
      },
    },
  },
};
